/**
 * TeacherProfileForm — controlled form section for the Teacher document.
 *
 * Renders Teacher-doc fields only (displayName, subjects, grades,
 * instructions, pastResults). User-doc fields (name, shortName, targetExams)
 * stay in the parent because each app handles them differently — the
 * teacher app bundles them into the same PUT, the admin console edits
 * them through a separate user-edit flow.
 *
 * Controlled component: parent owns `value` and the save button. Designed
 * to embed alongside other form sections.
 *
 * Used by:
 *  - Backend/teacher-app/src/pages/TeacherSettings.tsx (self-edit)
 *  - Backend/admin-console/src/pages/users/tabs/TeacherProfileTab.tsx
 *
 * Design doc: docs/FeatureDesigns/teacher-instructions.md
 */

import { SUBJECT_OPTIONS, GRADE_OPTIONS } from '@padcanvas/shared';
import { InfoTip } from '../components/InfoTip';
import { FormField } from '../components/FormField';
import { TextField } from '../components/TextField';
import { isFeatureEnabled } from '../feature-gates';
import './TeacherProfileForm.css';

export interface TeacherProfileFormValue {
    displayName?: string;
    subjects: string[];
    grades: string[];
    /** Kept on the document and still shown to students on the teacher card,
     *  but no longer editable here — the field is not currently part of the
     *  profile form. Existing values are preserved, not cleared. */
    bio?: string;
    /** Whether students can find this teacher when choosing a teacher.
     *  Absent means false (private) — see TeacherSchema.discoverable. */
    discoverable?: boolean;
    pastResults?: { year: string; exam: string; description: string }[];
    instructions?: {
        general?: string;
        hint?: string;
        analysis?: string;
        learningMoments?: string;
    };
}

/**
 * Which groups of fields to render. Defaults to all.
 *
 *   'display-name'  the name students see
 *   'teaching'      subjects + grades
 *   'visibility'    the discoverable checkbox
 *   'instructions'  AI prompt customizations
 *   'identity'      shorthand for the first three, as the whole profile block
 *
 * The three identity groups are addressable separately so a caller can lay
 * them out around fields it owns itself — the teacher app puts Display Name
 * on one row with the two User-doc name fields, and Target Exams on the next
 * row beside Subjects and Grades. Callers that want the whole block unchanged
 * keep passing 'identity'.
 */
export type TeacherProfileFormSection =
    | 'identity' | 'display-name' | 'teaching' | 'visibility' | 'instructions';

export interface TeacherProfileFormProps {
    value: TeacherProfileFormValue;
    onChange: (next: TeacherProfileFormValue) => void;
    disabled?: boolean;
    /** Override the default subject list (defaults to SUBJECT_OPTIONS). */
    availableSubjects?: readonly string[];
    /** Override the default grade list (defaults to GRADE_OPTIONS). */
    availableGrades?: readonly string[];
    /** Render only these section groups (defaults to all). */
    sections?: readonly TeacherProfileFormSection[];
    /** Drop the wrapper from layout (`display: contents`) so the fields become
     *  direct items of the CALLER's grid. Without it the wrapper is a single
     *  grid item and every field inside it stacks in that one column. */
    bare?: boolean;
}

type InstructionKey = keyof NonNullable<TeacherProfileFormValue['instructions']>;

// `experimental` fields render only when the `ai_instructions_experimental`
// gate is on (dev), so we can trial them without confusing prod teachers.
const INSTRUCTION_FIELDS: { key: InstructionKey; label: string; placeholder: string; experimental?: boolean }[] = [
    {
        key: 'general',
        label: 'General — applied to all student-facing prompts',
        placeholder: 'e.g. "Be warm and encouraging. Avoid technical jargon. Use short sentences."',
        experimental: true,
    },
    {
        key: 'hint',
        label: 'Hints',
        placeholder: 'e.g. "When giving hints, start with what the student got right, then point to the next step."',
    },
    {
        key: 'analysis',
        label: 'Answer analysis',
        placeholder: 'e.g. "Emphasize on the correctness and completeness of the steps"',
    },
    {
        key: 'learningMoments',
        label: 'Insights / re-teaching',
        placeholder: 'e.g. "Use concrete examples from everyday life. Tie back to a real-world scenario."',
        experimental: true,
    },
];

export function TeacherProfileForm({
    value,
    onChange,
    disabled,
    availableSubjects = SUBJECT_OPTIONS,
    availableGrades = GRADE_OPTIONS,
    sections = ['identity', 'instructions'],
    bare,
}: TeacherProfileFormProps) {
    const has = (s: TeacherProfileFormSection) =>
        sections.includes(s) || (s !== 'instructions' && sections.includes('identity'));
    const showDisplayName = has('display-name');
    const showTeaching = has('teaching');
    const showVisibility = has('visibility');
    const showInstructions = sections.includes('instructions');
    const set = <K extends keyof TeacherProfileFormValue>(key: K, next: TeacherProfileFormValue[K]) => {
        onChange({ ...value, [key]: next });
    };

    const toggleSubject = (subject: string) => {
        const current = value.subjects;
        set('subjects', current.includes(subject)
            ? current.filter(s => s !== subject)
            : [...current, subject]);
    };

    const toggleGrade = (grade: string) => {
        const current = value.grades;
        set('grades', current.includes(grade)
            ? current.filter(g => g !== grade)
            : [...current, grade]);
    };

    const updateInstruction = (key: InstructionKey, text: string) => {
        set('instructions', { ...(value.instructions || {}), [key]: text });
    };

    return (
        <div className={`teacher-profile-form${bare ? ' teacher-profile-form--bare' : ''}`}>
            {showDisplayName && (
            <TextField
                id="tpf-display-name"
                label="Display Name for Students"
                infoTip="Shown to your students"
                value={value.displayName || ''}
                onChange={e => set('displayName', e.target.value)}
                disabled={disabled}
            />
            )}

            {showTeaching && (
            <>
            <FormField label="Subjects">
                <div className="tpf-checkbox-group">
                    {availableSubjects.map(subject => (
                        <label key={subject} className="tpf-checkbox-label">
                            <input
                                type="checkbox"
                                checked={value.subjects.includes(subject)}
                                onChange={() => toggleSubject(subject)}
                                disabled={disabled}
                            />
                            <span>{subject.charAt(0).toUpperCase() + subject.slice(1)}</span>
                        </label>
                    ))}
                </div>
            </FormField>

            <FormField label="Grades">
                <div className="tpf-checkbox-group tpf-grade-group">
                    {availableGrades.map(grade => (
                        <label key={grade} className="tpf-checkbox-label">
                            <input
                                type="checkbox"
                                checked={value.grades.includes(grade)}
                                onChange={() => toggleGrade(grade)}
                                disabled={disabled}
                            />
                            {/* The number alone — the "Grades" label above the
                                group already says what these are, and repeating
                                it six times pushed the row onto two lines. */}
                            <span>{grade}</span>
                        </label>
                    ))}
                </div>
            </FormField>
            </>
            )}

            {showVisibility && (
            <FormField label="Visibility">
                {/* No box round this one. The bordered pill reads as "one of
                    several to pick from", which is what Subjects and Grades
                    are; a lone full-width setting is not. */}
                <label className="tpf-checkbox-label tpf-checkbox-label--plain">
                    <input
                        type="checkbox"
                        checked={value.discoverable === true}
                        onChange={e => set('discoverable', e.target.checked)}
                        disabled={disabled}
                    />
                    <span>Students can find me when choosing a teacher and send enrollment requests (each one needs my approval)</span>
                </label>
            </FormField>
            )}

            {showInstructions && (
            <div className="tpf-section">
                <label>Prompt Customizations <InfoTip text="Guidance the AI tutor follows when talking to your students. Each section applies only to the matching surface; leave blank to use the default." /></label>
                {INSTRUCTION_FIELDS
                    .filter(f => !f.experimental || isFeatureEnabled('ai_instructions_experimental'))
                    .map(({ key, label, placeholder }) => (
                    <div key={key} className="tpf-instruction-row">
                        <label className="tpf-sublabel" htmlFor={`tpf-instr-${key}`}>{label}</label>
                        <textarea
                            id={`tpf-instr-${key}`}
                            value={value.instructions?.[key] || ''}
                            onChange={e => updateInstruction(key, e.target.value)}
                            placeholder={placeholder}
                            rows={3}
                            disabled={disabled}
                        />
                    </div>
                ))}
            </div>
            )}
        </div>
    );
}
